/**
 * Merko Integrations — `Idempotency-Key` lifecycle.
 *
 * The handoff splits one word into two behaviours that must not be confused:
 *
 *   • A *deliberate* Sync now click gets a **new** key. So does retrying a
 *     terminal job, which starts a new full scan.
 *   • Retrying a possibly-lost HTTP *response* for that same request reuses the
 *     **same** key, so the server can recognise the duplicate.
 *
 * Reusing a key against a different profile returns `idempotency_conflict`, so
 * a key is held per profile and dropped the moment a response arrives.
 */

/** Backend accepts 8–128 characters. */
const MIN_LENGTH = 8;
const MAX_LENGTH = 128;

/** Keys for requests whose response has not come back yet, per profile. */
const inFlight = new Map<string, string>();

function randomKey(): string {
    const cryptoObj = typeof crypto !== "undefined" ? crypto : undefined;
    if (cryptoObj?.randomUUID) return cryptoObj.randomUUID().replace(/-/g, "");
    if (cryptoObj?.getRandomValues) {
        const bytes = cryptoObj.getRandomValues(new Uint8Array(16));
        return Array.from(bytes, (b) => b.toString(16).padStart(2, "0")).join("");
    }
    // Last resort; still comfortably inside the length bounds.
    return `${Date.now().toString(36)}${Math.random().toString(36).slice(2)}`.padEnd(MIN_LENGTH, "0");
}

export function isValidIdempotencyKey(key: string): boolean {
    return key.length >= MIN_LENGTH && key.length <= MAX_LENGTH;
}

/**
 * The key to send for a run of `profileId`.
 *
 * Returns the held key when a previous attempt for this profile never got a
 * response — that is the lost-response retry the handoff describes. Otherwise
 * mints a new one, which is what a fresh deliberate click deserves.
 */
export function idempotencyKeyFor(profileId: string): string {
    const held = inFlight.get(profileId);
    if (held) return held;

    const key = randomKey().slice(0, MAX_LENGTH);
    inFlight.set(profileId, isValidIdempotencyKey(key) ? key : key.padEnd(MIN_LENGTH, "0"));
    return inFlight.get(profileId)!;
}

/**
 * A response arrived, so the key has served its purpose.
 *
 * Called for a failure as well as a success: once the server has answered, the
 * next click is a new intent and must not inherit this key.
 */
export function releaseIdempotencyKey(profileId: string): void {
    inFlight.delete(profileId);
}

/**
 * Force the next run of this profile to mint a fresh key.
 *
 * Used for a terminal-job retry, which the handoff says needs a **new** key
 * because it starts a new full scan.
 */
export function resetIdempotencyKey(profileId: string): string {
    inFlight.delete(profileId);
    return idempotencyKeyFor(profileId);
}

/** Test seam. */
export function __clearIdempotencyKeys(): void {
    inFlight.clear();
}
